import type { ReactNode } from 'react';
import { Breadcrumb as AntBreadcrumb, Button, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { typeScale, space } from './tokens';
import { useNarrowViewport } from './useNarrowViewport';

/**
 * A page's top strip: breadcrumb, back control, title, and a right-aligned
 * action slot. Router-agnostic BY CONSTRUCTION — navigation is emitted through
 * `onNavigate`/`onBack` callbacks, never a rendered router <Link>, so a consumer
 * (DetailPage) can intercept it (e.g. to guard unsaved changes). packages/ui
 * must not import react-router.
 */
export interface Breadcrumb {
  label: string;
  /** Present on link crumbs; omit on the current (last, non-link) crumb. */
  href?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  breadcrumbs?: Breadcrumb[];
  subtitle?: ReactNode;
  /** Right-aligned actions (Edit, Archive, …). */
  actions?: ReactNode;
  /** Renders a back affordance when set. */
  onBack?: () => void;
  /** Invoked for a LINK crumb (one with an href). The app performs the nav. */
  onNavigate?: (target: { href: string }) => void;
}

export function PageHeader({
  title,
  breadcrumbs,
  subtitle,
  actions,
  onBack,
  onNavigate,
}: PageHeaderProps) {
  // On a phone the actions drop below the title rather than squeezing it to a
  // word per line or running off the screen.
  const narrow = useNarrowViewport();
  return (
    <div style={{ marginBottom: space.lg }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <AntBreadcrumb
          style={{ marginBottom: space.sm }}
          items={breadcrumbs.map((c) =>
            c.href
              ? {
                  title: (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => onNavigate?.({ href: c.href! })}
                    >
                      {c.label}
                    </Button>
                  ),
                }
              : { title: <Typography.Text type="secondary">{c.label}</Typography.Text> },
          )}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: space.md,
          flexWrap: narrow ? 'wrap' : undefined,
        }}
      >
        <Space align="center" size={8}>
          {onBack && (
            <Button
              type="text"
              aria-label="Back"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
            />
          )}
          <div>
            <Typography.Title level={4} style={{ margin: 0, fontSize: typeScale.h4 }}>
              {title}
            </Typography.Title>
            {subtitle && <Typography.Text type="secondary">{subtitle}</Typography.Text>}
          </div>
        </Space>
        {actions && (
          // Wraps at every width: a no-op while the buttons fit, and at tablet
          // width a page with several actions stacks them instead of running
          // off the screen. Wrapped rows stay right-aligned off a phone.
          <Space size={8} wrap style={narrow ? undefined : { justifyContent: 'flex-end' }}>
            {actions}
          </Space>
        )}
      </div>
    </div>
  );
}
